<script>
import dayjs from 'dayjs';
import { formatNumber } from '@ubic/utils/formatters';
import IconClock from '@/components/icons/Clock';
import IconUserFriends from '@/components/icons/UserFriends';
import IconGhost from '@/components/icons/Ghost';
import WithCSSTooltip from '@/components/WithCSSTooltip';
import DateRangeSelect from '@/components/DateRangeSelect';

export default {
  props: {
    getDataService: {
      type: Function,
      required: true,
    },
  },
  data () {
    return {
      isFetching: false,
      data: [],

      filter: {
        minDate: dayjs().subtract(7, 'day').format('YYYY-MM-DD'),
        maxDate: dayjs().format('YYYY-MM-DD'),
      },
    };
  },
  methods: {
    getLinkWithFilter (path) {
      return {
        path,
        query: this.filter,
      };
    },
    async handleDateRangeChange (dates) {
      this.filter = {
        ...this.filter,
        ...dates,
      };

      this.$router.replace({
        ...this.$route,
        query: {
          ...this.$route.query,
          projectFilter: `${dates.minDate},${dates.maxDate}`,
        },
      });

      this.data = await this.getDataService(this.filter);
    },
  },
  async created () {
    this.isFetching = true;

    if (this.$route.query.projectFilter) {
      const [minDate, maxDate] = this.$route.query.projectFilter.split(',');

      this.filter = {
        ...this.filter,
        minDate,
        maxDate,
      };
    }

    this.data = await this.getDataService(this.filter);

    this.isFetching = false;
  },
  render () {
    if (this.isFetching) {
      return <u-spinner />;
    }

    const { $style, getLinkWithFilter } = this;

    return (
      <div>
        <header class="page-header">
          <u-title level="2">
            Активные проекты
          </u-title>

          <DateRangeSelect
            value={this.filter}
            onChange={this.handleDateRangeChange}
          />
        </header>

        <div class={$style.list}>
          {this.data.map(project => (
            <div
              key={project.id}
              class={$style.project}
            >
              <div class={$style.projectInfo}>
                <span class={$style.projectInfoName}>
                  <router-link
                    to={getLinkWithFilter(`/statistics/project/${project.id}`)}
                  >
                    {project.name}
                  </router-link>
                </span>
                <span>
                  <IconUserFriends class={$style.projectInfoIcon} />
                  {project.workersCount}
                </span>
                <span class={$style.projectInfoHours}>
                  <IconClock class={$style.projectInfoIcon} />
                  {formatNumber(Math.round(project.totalHours))}
                </span>
              </div>

              <div class={$style.projectDepartments}>
                {project.departments.map(department => {
                  const width = department.totalHours / project.totalHours;

                  return (
                    <WithCSSTooltip
                      key={department.name}
                      class={$style.projectDepartmentsItem}
                      style={{
                        color: department.color,
                        width: `${100 * width}%`,
                      }}
                    >
                      <span
                        slot="tooltip"
                        class={$style.projectDepartmentsItemTooltip}
                      >
                        {department.name}
                        {' '}
                        <span class={$style.projectInfoHours}>
                          <IconClock class={$style.projectInfoIcon} />
                          {formatNumber(Math.round(department.totalHours))}
                        </span>
                      </span>
                    </WithCSSTooltip>
                  );
                })}
              </div>
            </div>
          ))}

          {!this.data.length && (
            <div class={$style.listEmpty}>
              <div class={$style.listEmptyIcon}>
                <IconGhost />
              </div>
              Нет активности за выбранный период
            </div>
          )}
        </div>
      </div>
    );
  },
};
</script>

<style lang="scss" module>
@import '@/theme/include.scss';

.list {
  display: grid;
  grid-column-gap: 1em;
  grid-row-gap: 1em;
  grid-template-columns: 1fr 1fr 1fr;
  margin: 0 -1em;

  &Empty {
    align-items: center;
    color: $color-gray;
    display: flex;
    flex-direction: column;
    grid-column: 1 / span 3;
    justify-content: center;
    padding: 32px;
    text-align: center;

    &Icon {
      color: $color-gray-lighter;
      font-size: 5em;
      margin: .125em 0;
    }
  }
}

.project {
  background-color: $color-white;
  border: 1px solid $color-gray-lighter;
  border-radius: .25em;
  display: block;
  padding: .75em;

  &Info {
    align-items: center;
    display: flex;

    &Icon {
      font-size: .75em;
      margin-right: .5em;
    }

    &Name {
      margin-right: auto;
    }

    &Workers {
      margin-left: 1em;
    }

    &Hours {
      margin-left: 1em;
    }
  }

  &Departments {
    align-items: stretch;
    display: flex;
    height: 1em;
    justify-content: stretch;
    margin-top: .5em;

    &:empty {
      background-color: $color-gray-lighter;
      border-radius: .25em;
    }

    &Item {
      background-color: currentColor;
      flex-grow: 0;
      flex-shrink: 1;
      min-width: .5em;
      transition: width .2s;

      &:first-child {
        border-bottom-left-radius: .25em;
        border-top-left-radius: .25em;
      }

      &:last-child {
        border-bottom-right-radius: .25em;
        border-top-right-radius: .25em;
      }

      &Tooltip {
        color: $color-black;
        font-size: .825em;
        white-space: nowrap;
      }
    }
  }
}
</style>
